import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, UserCheck, ArrowLeft, KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/status";
import { useReauthCall } from "@/lib/reauth";
import { usePractice } from "@/pages/StaffApp";
import { ManagePinDialog } from "@/components/ManagePinDialog";

function ReviewFrame({ pid }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/packets/${pid}/review.pdf`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => toast.error(errMsg(e)));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [pid]);
  if (!url) return <div className="h-[60vh] grid place-items-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (<div className="space-y-1">
    <iframe title="Completed consent for review" src={url} className="w-full h-[60vh] rounded-lg border bg-white" data-testid="verify-review-pdf" />
    <a href={url} target="_blank" rel="noreferrer" className="text-sm text-[var(--brand)] underline" data-testid="verify-review-open">Open full consent in a new tab</a>
  </div>);
}

function PatientBanner({ p }) {
  return (
    <div className="rounded-xl border-2 border-[var(--brand)] bg-[var(--brand-soft)] p-4 grid sm:grid-cols-4 gap-2 text-sm" data-testid="verify-patient-banner">
      <div><p className="text-xs text-slate-500">Patient</p><p className="font-bold text-base" data-testid="verify-patient-name">{p.patient_name}</p></div>
      <div><p className="text-xs text-slate-500">Date of birth</p><p className="font-semibold">{fmtDate(p.patient_dob)}</p></div>
      <div><p className="text-xs text-slate-500">Chart</p><p className="font-semibold">{p.chart_id || "-"}</p></div>
      <div><p className="text-xs text-slate-500">Signed by</p><p className="font-semibold">{p.signer?.name} ({p.signer?.type}) {fmt(p.events?.patient_signed)}</p></div>
    </div>
  );
}

function AttestForm({ p, cfg, onDone }) {
  const [ok, setOk] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinDlg, setPinDlg] = useState(false);
  const call = useReauthCall("post", `/packets/${p.id}/provider-verify`);
  const needPin = cfg.method === "pin";
  const ready = ok && (!needPin || /^\d{6}$/.test(pin)) && !busy;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await call({ attest: true, statement_version: cfg.statement.version, patient_id: p.patient_id, packet_version: p.version, pin: needPin ? pin : undefined });
      toast.success(r.status === "signed" ? "Consent verified and finalized" : `Verified - status ${r.status}`);
      onDone();
    } catch (e) { setPin(""); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-4 space-y-4 bg-white" data-testid="verify-attest-form">
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={ok} onCheckedChange={(v) => setOk(v === true)} className="mt-1" data-testid="verify-attest-checkbox" />
        <span className="text-sm leading-relaxed" data-testid="verify-statement">{cfg.statement.text} <span className="text-xs text-slate-400">({cfg.statement.version})</span></span>
      </label>
      {needPin && <div className="flex flex-wrap items-center gap-2">
        <Input type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" className={`w-56 h-11 ${pin ? "tracking-[0.4em]" : ""}`} data-testid="verify-pin-input" />
        <Button variant="link" size="sm" onClick={() => setPinDlg(true)} data-testid="verify-manage-pin-link"><KeyRound className="w-3.5 h-3.5 mr-1" />Set up or reset PIN</Button>
      </div>}
      {cfg.method === "reauth" && <p className="text-xs text-slate-500">You will be asked to re-enter your sign-in credentials if you have not done so in the last {cfg.reauth_minutes} minutes.</p>}
      <p className="text-xs text-slate-500">This records an attestation of verification. It is not a provider signature.</p>
      <Button className="h-12 px-6" disabled={!ready} onClick={submit} data-testid="verify-finalize-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}Finalize Consent</Button>
      <ManagePinDialog open={pinDlg} onOpenChange={setPinDlg} />
    </div>
  );
}

export default function VerifyPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { role, practice } = usePractice();
  const [p, setP] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get(`/packets/${pid}`).then((r) => setP(r.data.packet || r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/practice/verification").then((r) => setCfg(r.data)).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);
  const back = <Button variant="outline" onClick={() => nav("/", { replace: true })} data-testid="verify-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to worklist</Button>;
  if (err) return <div className="p-8 space-y-4"><p className="text-red-700" data-testid="verify-error">{err}</p>{back}</div>;
  if (!p || !cfg) return <div className="p-8 text-slate-500">Loading...</div>;
  const awaiting = p.status === "awaiting_provider";
  return (
    <div className="h-full overflow-y-auto" data-testid="verify-page">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><p className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" />Provider-only screen - {practice.name}</p>
            <h1 className="text-2xl sm:text-3xl font-bold">Provider verification</h1></div>
          {back}
        </div>
        <PatientBanner p={p} />
        {!awaiting && <p className="rounded-lg bg-slate-100 p-3 text-sm" data-testid="verify-not-awaiting">This consent is not awaiting provider verification (status: {p.status}).</p>}
        {awaiting && role !== "provider" && <p className="rounded-lg bg-amber-50 text-amber-900 p-3 text-sm" data-testid="verify-provider-only">Only the patient's provider can verify and finalize this consent. Hand the device to the provider and have them sign in, or they can verify it later from their own worklist.</p>}
        {awaiting && <ReviewFrame pid={p.id} />}
        {awaiting && role === "provider" && <AttestForm p={p} cfg={cfg} onDone={() => nav("/", { replace: true })} />}
      </div>
    </div>
  );
}
